/**
 * InvoiceDetailPage - S5, the detail of one invoice (SIH-17 I-8c, TL A3;
 * UX-T16 page 07).
 *
 * Reached from a row of the failed (S3) or pending (S4) list. The invoice is found
 * in the branch's invoice list for the URL's period (see invoiceDetailStore). The
 * header freshness is the branch's own row in the TPIN's branch list, as on S3
 * and S4. Refresh is the only re-read (PD-33) and re-queries this branch only: no
 * period selector and no Change TPIN here. Back goes to the list the invoice
 * belongs to by its status. An invoice the period's list does not hold, a failed
 * read and an unusable range each have their own named state; none is blank.
 */

import { useEffect, useState } from 'react'
import { AppHeader } from '@components/AppHeader'
import { BranchRefresh } from '@components/BranchRefresh'
import { Breadcrumbs, type Crumb } from '@components/Breadcrumbs'
import { InvoiceDetailView } from '@components/InvoiceDetailView'
import { Link } from '@components/Link'
import { ErrorState, LoadingState, RefreshingFrame } from '@components/RealStates'
import { routePeriodUsable, useRoute } from '@components/route'
import { useBranchContext } from '@components/useBranchContext'
import { backTarget, detailBody, listCrumb } from '@domain/invoiceDetail'
import { branchFreshness } from '@domain/tpinBranch'
import { branchPath, tpinPath } from '@domain/tpinPaths'
import { leaveInvoice } from '@lib/scrollMemory'
import { useInvoiceDetailStore } from '@stores/invoiceDetailStore'

export { REFRESH_NOTE } from '@components/BranchRefresh'

export function notFoundText(rectNo: number, dbName: string): string {
  return `Invoice ${rectNo} was not found in branch ${dbName} for this period.`
}

export function InvoiceDetailPage({
  tin,
  dbName,
  rectNo,
}: {
  readonly tin: string
  readonly dbName: string
  readonly rectNo: number
}) {
  const route = useRoute()
  const period = route.period
  const verdict = routePeriodUsable(period)
  // An unusable range is the empty string: no real date is empty, so no read is made for it.
  const from = verdict.usable ? verdict.resolved.from : ''
  const to = verdict.usable ? verdict.resolved.to : from

  const view = useInvoiceDetailStore((s) => s.view)
  const open = useInvoiceDetailStore((s) => s.open)
  const refreshDetail = useInvoiceDetailStore((s) => s.refresh)
  const retry = useInvoiceDetailStore((s) => s.retry)
  const cancel = useInvoiceDetailStore((s) => s.cancel)
  const branch = useBranchContext(tin, dbName, from, to)

  useEffect(() => {
    if (from === '') return
    open(dbName, { from, to }, rectNo)
    return cancel
  }, [dbName, from, to, rectNo, open, cancel])

  // Going back to the list it came from keeps the list's scroll position; any other way out forgets it.
  useEffect(() => () => leaveInvoice(dbName, window.location.pathname), [dbName])

  const body = detailBody(view)
  const back = backTarget(body.kind === 'found' ? body.row : null, tin, dbName)
  const crumbs: Crumb[] = [
    { label: `TPIN ${tin}`, to: tpinPath(tin) },
    { label: `Branch ${dbName}`, to: branchPath(tin, dbName) },
    ...listCrumb(back),
    { label: `Invoice ${rectNo}` },
  ]

  // The reference clock for the relative ages: fixed at mount, as on the lists (no live ticking).
  const [now] = useState(() => new Date())
  const refreshing = view.kind === 'refreshing' || branch.refreshing

  const handleRefresh = () => {
    if (from === '') return
    branch.refresh()
    refreshDetail()
  }

  return (
    <div className="page page-invoice" data-testid="invoice-page">
      <AppHeader period={period} />
      <main className="page-main">
        <BranchRefresh freshness={branchFreshness(branch.lookup)} refreshing={refreshing} onRefresh={handleRefresh} />

        <Breadcrumbs crumbs={crumbs} period={period} />
        <h1 className="page-title" data-testid="invoice-title">{`Invoice ${rectNo}`}</h1>
        <p className="dash-back">
          <Link to={back.path} period={period} className="dash-hero-change" testid="invoice-back">
            {back.label}
          </Link>
        </p>

        {!verdict.usable ? (
          <ErrorState message={verdict.reason} />
        ) : (
          <RefreshingFrame refreshing={view.kind === 'refreshing'}>
            {body.kind === 'loading' && <LoadingState label={`Loading invoice ${rectNo}…`} />}
            {body.kind === 'error' && <ErrorState message={body.message} onRetry={retry} />}
            {body.kind === 'not-found' && (
              <div className="state-empty" role="status" data-testid="invoice-notfound">
                <p>{notFoundText(rectNo, dbName)}</p>
              </div>
            )}
            {body.kind === 'found' && <InvoiceDetailView row={body.row} dbName={dbName} from={from} to={to} now={now} />}
          </RefreshingFrame>
        )}
      </main>
    </div>
  )
}
